/* ==========================================================================
   USA Factory page (template: page.factory)

   Two behaviours, both vanilla (no jQuery):
     - gbusaInitVideo    : click-to-play inline video with a poster/scrim overlay
     - gbusaInitCarousel : scroll-snap rail (mobile only; desktop is a CSS grid)

   Both are idempotent (guarded by data-gbusa-ready) and re-run on
   `shopify:section:load`, because the theme editor re-renders sections in place.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- video */
  function initVideo(frame) {
    if (frame.dataset.gbusaReady) return;
    frame.dataset.gbusaReady = '1';

    var video = frame.querySelector('video');
    var playBtn = frame.querySelector('[data-gbusa-play]');
    if (!video) return;

    function play() {
      // Only one video on the page makes noise at a time. Ambient step loops
      // are excluded: they are muted, and pausing them here would strand them
      // (their observer only fires on an intersection CHANGE, so a visible
      // card would never resume).
      var others = document.querySelectorAll(
        '.usa-factory-page video:not(.gbusa-steps__video):not(.gbusa-hero__video)'
      );
      for (var i = 0; i < others.length; i++) {
        if (others[i] !== video && !others[i].paused) others[i].pause();
      }

      video.muted = false;
      video.controls = true;
      frame.classList.add('is-playing');

      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          // Autoplay policy can still refuse; surface the controls and let the
          // user press the native play button rather than failing silently.
          video.controls = true;
        });
      }
    }

    if (playBtn) playBtn.addEventListener('click', play);

    // NOTE: no 'pause' handler restores the overlay. Once the clip is engaged,
    // the poster/play-button/caption stay hidden so they never overlap the
    // native controls a paused video shows. The overlay only returns on ended.
    video.addEventListener('ended', function () {
      frame.classList.remove('is-playing');
      video.controls = false;
      video.currentTime = 0;
    });

    // Pause when scrolled out of view. Deliberately a pauser only, never an
    // autoplayer — these videos carry narration.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (!entries[i].isIntersecting && !video.paused) video.pause();
          }
        },
        { threshold: 0.25 }
      ).observe(video);
    }

    // Fill an empty duration chip from the file's own metadata.
    var chip = frame.querySelector('[data-gbusa-duration]');
    if (chip && !chip.textContent.trim()) {
      video.addEventListener('loadedmetadata', function () {
        var d = video.duration;
        if (!isFinite(d) || d <= 0) return;
        var mins = Math.floor(d / 60);
        var secs = Math.floor(d % 60);
        chip.textContent = mins + ':' + (secs < 10 ? '0' + secs : secs);
      });
    }
  }

  /* ------------------------------------------------------------- carousel */
  function initCarousel(root) {
    if (root.dataset.gbusaReady) return;
    root.dataset.gbusaReady = '1';

    var rail = root.querySelector('[data-gbusa-rail]');
    var next = root.querySelector('[data-gbusa-next]');
    if (!rail) return;

    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-gbusa-dot]'));
    var count = rail.children.length;
    var active = 0;

    function setActive(i) {
      if (i === active || i < 0 || i >= count) return;
      active = i;
      for (var n = 0; n < dots.length; n++) {
        var on = n === i;
        dots[n].classList.toggle('is-active', on);
        dots[n].setAttribute('aria-current', on ? 'true' : 'false');
      }
    }

    function goTo(i) {
      var left = i * rail.clientWidth;
      if (typeof rail.scrollTo === 'function') {
        rail.scrollTo({ left: left, behavior: 'smooth' });
      } else {
        rail.scrollLeft = left;
      }
    }

    rail.addEventListener(
      'scroll',
      function () {
        if (!rail.clientWidth) return; // desktop grid: never scrolls
        var i = Math.round(rail.scrollLeft / rail.clientWidth);
        setActive(Math.max(0, Math.min(count - 1, i)));
      },
      { passive: true }
    );

    if (next) {
      next.addEventListener('click', function () {
        goTo((active + 1) % count);
      });
    }

    dots.forEach(function (dot, n) {
      dot.addEventListener('click', function () {
        goTo(n);
      });
    });
  }

  /* ------------------------------------------------- ambient step videos */
  /* Silent looping clips in the "how it's made" cards. They ship with
     preload="none" so four videos never sit on the critical path; each one is
     fetched and played the first time its card is on screen, and paused when
     it leaves. Users who ask for reduced motion get a still first frame. */
  function initAmbient(video) {
    if (video.dataset.gbusaReady) return;
    video.dataset.gbusaReady = '1';

    var reduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!('IntersectionObserver' in window)) {
      // No observer: just load the poster frame and leave it be.
      video.preload = 'metadata';
      return;
    }

    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            if (video.preload === 'none') {
              video.preload = 'auto';
              video.load();
            }
            if (reduced) return; // show the frame, don't animate it
            var p = video.play();
            if (p && typeof p.catch === 'function') p.catch(function () {});
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: 0.2 }
    ).observe(video);
  }

  /* ----------------------------------------------------------------- boot */
  function boot(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-gbusa-video]').forEach(initVideo);
    root.querySelectorAll('[data-gbusa-carousel]').forEach(initCarousel);
    root.querySelectorAll('.gbusa-steps__video').forEach(initAmbient);
  }

  if (document.readyState !== 'loading') {
    boot();
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  }

  // Theme editor re-renders a section's DOM on every settings change.
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
